import type { GetOrganizationsMetadataQuery } from '@pypestream/api-services/urql';

/**
 * Per-org `canManageAccess` resolution (PSTUDIO-3111).
 *
 * The backend is the source of truth for authorization: each org's
 * `canManageAccess` comes from `organizationsMetadata(accountIds).canManageAccess`.
 * These helpers only normalise and aggregate those backend responses — they do
 * NOT compute permissions on the client. Kept dependency-light (type-only import)
 * so unit tests do not pull in the design-system runtime.
 */
export type OrganizationMetadataResult = {
  accountId: string;
  canManageAccess: boolean;
};

/** The backend rejects a larger `accountIds` list (BAD_USER_INPUT). */
export const ORGANIZATIONS_METADATA_BATCH_SIZE = 100;

// One batched organizationsMetadata query per chunk of at most 100 orgs
// (PSTUDIO-4741; it replaced one GetOrganizationMetadata per org).
type GetOrganizationsMetadata = (vars: {
  accountIds: string[];
}) => PromiseLike<{ data?: GetOrganizationsMetadataQuery; error?: unknown }>;

export type CanManageAccessResolution = {
  /** Every org's flag, failed lookups denied — what the UI reads. */
  byOrgId: Record<string, boolean>;
  /** Fresh seeds plus successful lookups only — what may be cached
   * (PSTUDIO-4293). A failed lookup is left out so the next load retries it
   * instead of caching the deny, and cached seeds are not echoed back so a
   * snapshot can't overwrite a fresher value stored meanwhile. */
  resolved: Record<string, boolean>;
};

/**
 * `cached`: flags from the session cache — not re-queried, not handed back.
 * `fresh`: flags just read from the backend (the route org's, from
 * GetAccountDetails) — not re-queried, override `cached`, handed back.
 */
export const resolveCanManageAccess = async (
  orgs: { id?: string | null }[],
  getOrganizationsMetadata: GetOrganizationsMetadata,
  {
    cached = {},
    fresh = {},
  }: { cached?: Record<string, boolean>; fresh?: Record<string, boolean> } = {}
): Promise<CanManageAccessResolution> => {
  const accountIds = [
    ...new Set(
      orgs
        .map((org) => org.id)
        .filter((id): id is string => !!id && !(id in cached) && !(id in fresh))
    ),
  ];
  const chunks: string[][] = [];
  for (
    let i = 0;
    i < accountIds.length;
    i += ORGANIZATIONS_METADATA_BATCH_SIZE
  ) {
    chunks.push(accountIds.slice(i, i + ORGANIZATIONS_METADATA_BATCH_SIZE));
  }

  // Promise.allSettled (not all): one failed chunk must not break the whole
  // page. The backend fails a whole batch on any error, so a chunk is the unit
  // of failure: its orgs fall back to canManageAccess:false (fail-closed —
  // deny, never grant, on error). urql does not reject on a network/GraphQL
  // error: it resolves with `error` set, so that counts as a failure too.
  const settled = await Promise.allSettled(
    chunks.map((chunk) =>
      getOrganizationsMetadata({ accountIds: chunk }).then((res) => {
        if (res.error || !res.data) throw res.error ?? new Error('no data');
        return res.data.admin_?.organizationsMetadata ?? [];
      })
    )
  );

  // Only ids that were asked for and came back count as looked up; a missing
  // one is denied like a failure and, like a failure, not cached.
  const fulfilled: OrganizationMetadataResult[] = settled.flatMap(
    (outcome, index) => {
      if (outcome.status !== 'fulfilled') return [];
      const asked = new Set(chunks[index]);
      return outcome.value.flatMap(({ accountId, canManageAccess }) =>
        accountId && asked.has(accountId)
          ? [{ accountId, canManageAccess: !!canManageAccess }]
          : []
      );
    }
  );
  // An id the response repeats is granted only if every copy grants it — a
  // malformed response is denied, never resolved by "last one wins".
  const fetched = fulfilled.reduce<Record<string, boolean>>(
    (acc, { accountId, canManageAccess }) => {
      acc[accountId] = (acc[accountId] ?? true) && canManageAccess;
      return acc;
    },
    {}
  );
  const denied = Object.fromEntries(accountIds.map((id) => [id, false]));

  return {
    byOrgId: { ...cached, ...fresh, ...denied, ...fetched },
    resolved: { ...fresh, ...fetched },
  };
};

/** The slice of the manager context that holds the session cache. */
export type CanManageAccessCache = {
  canManageAccessByOrgId: Record<string, boolean>;
  canManageAccessCacheEpoch: number;
};

/** What a lookup service hands back for the cache: its cacheable flags and the
 * epoch it read when it started. */
export type CanManageAccessCacheUpdate = {
  resolved: Record<string, boolean>;
  epoch: number;
};

/**
 * Folds a lookup's cacheable flags into the session cache. `update.epoch` is
 * the cache epoch the lookup read when it began: if the cache was invalidated
 * while it was in flight, its result predates the change and is dropped.
 */
export const mergeCanManageAccessCache = (
  cache: Record<string, boolean>,
  update: CanManageAccessCacheUpdate,
  currentEpoch: number
): Record<string, boolean> =>
  update.epoch === currentEpoch ? { ...cache, ...update.resolved } : cache;

export const applyCanManageAccessCacheUpdate = (
  ctx: CanManageAccessCache,
  update: CanManageAccessCacheUpdate | undefined
) => {
  if (!update) return;
  ctx.canManageAccessByOrgId = mergeCanManageAccessCache(
    ctx.canManageAccessByOrgId,
    update,
    ctx.canManageAccessCacheEpoch
  );
};

/** Drops every cached flag; called when the logged-in user's access may have
 * changed (org created/deleted, own roles or a team saved, user switched). */
export const invalidateCanManageAccessCache = (ctx: CanManageAccessCache) => {
  ctx.canManageAccessByOrgId = {};
  ctx.canManageAccessCacheEpoch += 1;
};

/**
 * Per-org authorization lookup used by the user-details mutation guards.
 * Returns the backend `canManageAccess` for a single org from the rows already
 * resolved into the viewed user's state. Fail-closed: unknown org / missing
 * flag → false. Pure (no design-system import) so it is unit-testable.
 */
export const getOrgCanManageAccess = (
  orgs:
    | ReadonlyArray<{ id?: string | null; canManageAccess?: boolean }>
    | undefined,
  orgId: string | null | undefined
): boolean =>
  !!orgId && !!orgs?.find((org) => org.id === orgId)?.canManageAccess;

/** Minimal structural shape of a permissions-table org row needed to map a
 * project id to its org (assignedProjects carry the project nested, available
 * projects are the raw account project rows). */
type OrgProjectAccessRow = {
  id?: string | null;
  canManageAccess?: boolean;
  assignedProjects?: ReadonlyArray<{
    project?: { id?: string | null } | null;
  } | null> | null;
  availableProjects?: ReadonlyArray<{ id?: string | null } | null> | null;
};

const orgContainsProject = (
  org: OrgProjectAccessRow,
  projectId: string
): boolean =>
  !!org.assignedProjects?.some((p) => p?.project?.id === projectId) ||
  !!org.availableProjects?.some((p) => p?.id === projectId);

/**
 * Per-org gate for persisting project-role updates: keeps only the entries
 * whose project belongs to an org the logged-in user can manage. Needed
 * because updates can span orgs (per-org guards allow child-org edits, and
 * the consolidation path generates entries without going through the guards)
 * while `setUserAccountProjectsRoles` was previously all-or-nothing on the
 * route org's flag. Fail-closed: entries whose project cannot be mapped to a
 * manageable org are dropped.
 */
export const filterProjectRolesUpdateByOrgAccess = <
  T extends { id?: string | null },
>(
  orgs: ReadonlyArray<OrgProjectAccessRow> | undefined,
  projectRolesUpdate: ReadonlyArray<T> | undefined
): T[] =>
  (projectRolesUpdate || []).filter(
    (update) =>
      !!update.id &&
      !!orgs?.find((org) => orgContainsProject(org, update.id!))
        ?.canManageAccess
  );
